import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './AdvertisingRequest.css';
import { readWorkspace, writeWorkspace } from '../lib/workspaceStorage';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/advertising`;
const money = value => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
export const splitBudget = value => {
  const total = Math.round(Number(value) * 100);
  if (!Number.isSafeInteger(total) || total <= 0) return null;
  const fee = Math.round(total / 5);
  return { total: total / 100, agency: fee / 100, media: (total - fee) / 100 };
};
const SERVICE = {
  ads: { title: 'Reklam Yönetimi', description: 'Bütçenin %80’i reklam yayınına, %20’si ajans yönetimine ayrılır.' },
  influencer_video: { title: 'Influencer Video', description: 'Sadece video üretimi için teklif ve uygunluk talep et.' },
  influencer_video_share: { title: 'Influencer Video + Sayfa Paylaşımı', description: 'Video üretimiyle birlikte sayfa paylaşımı için teklif ve yayın uygunluğu talep et.' },
};
const statusLabel = {
  accepted: 'Talep e-posta hizmeti tarafından kabul edildi; ekip değerlendirmesi bekleniyor.',
  failed: 'Talep kayıtlı, e-posta gönderimi doğrulanamadı; gönderim otomatik olarak yeniden denenecek.',
  needs_review: 'Talep kayıtlı; gönderim için destek ekibinin kontrolü gerekiyor.',
  pending: 'Talep kayıtlı, e-posta gönderimi bekleniyor.',
  sending: 'E-posta gönderimi işleniyor; durumu yenileyebilirsin.',
};
const FIELD = { title: 'Ad', region: 'Şehir / ilçe', target_url: 'Bağlantı', start_date: 'Başlangıç tarihi', event_date: 'Etkinlik tarihi', budget_try: 'Bütçe', phone: 'Telefon', contact_email: 'E-posta', brief: 'Açıklama' };
const errorMessage = error => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(item => `${FIELD[item.loc?.at(-1)] || 'Form'}: ${item.msg || 'Bilgiyi kontrol et.'}`).join(' ');
  return 'İşlem tamamlanamadı. Bilgilerini kontrol edip tekrar dene.';
};

export default function AdvertisingRequest(props) {
  const { user } = useAuth();
  return <RequestForm key={user?.id || user?.email} {...props} />;
}

function RequestForm({ embedded = false, onRecorded, prefill = {} }) {
  const { user } = useAuth();
  const pendingKey = `promotion-pending:${user?.id || user?.email}`;
  const [savedPending] = useState(() => readWorkspace(pendingKey));
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [authorized, setAuthorized] = useState(Boolean(savedPending));
  const [pending, setPending] = useState(savedPending);
  const [review, setReview] = useState(false);
  const [form, setForm] = useState(savedPending || {
    service: 'ads',
    kind: user?.role === 'venue' ? 'venue' : 'ticket_sales',
    title: '', contact_name: user?.name || '', contact_email: user?.email || '', phone: '',
    region: '', budget_try: '', start_date: '', event_date: '', target_url: '', brief: '',
    ...Object.fromEntries(Object.entries(prefill).filter(([key, value]) => ['title', 'region', 'budget_try', 'event_date', 'brief'].includes(key) && typeof value === 'string'))
  });
  const isAds = form.service === 'ads';
  const budget = isAds ? splitBudget(form.budget_try) : null;
  const load = async () => {
    setLoading(true); setError('');
    try {
      const [cfg, records] = await Promise.all([axios.get(`${API}/config`), axios.get(`${API}/requests`)]);
      setConfig(cfg.data); setHistory(records.data);
    } catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const change = e => setForm(previous => ({ ...previous, [e.target.name]: e.target.value }));
  const submit = async (e, confirmed = false) => {
    e.preventDefault();
    if (busy || !authorized || !config?.available || (isAds && !budget)) return;
    if (embedded && !pending && !confirmed) { setReview(true); return; }
    setBusy(true); setError('');
    const payload = pending || {
      ...form,
      request_id: crypto.randomUUID(),
      budget_try: isAds ? form.budget_try : null,
      event_date: form.kind === 'ticket_sales' ? form.event_date : null,
      share_authorized: true,
      consent_token: config.consent_token
    };
    writeWorkspace(pendingKey, payload);
    setPending(payload);
    try {
      const { data } = await axios.post(`${API}/requests`, payload);
      setResult(data); setHistory(previous => [data, ...previous.filter(item => item.id !== data.id)]);
      writeWorkspace(pendingKey, null);
      setPending(null); setReview(false); onRecorded?.(data);
    } catch (e) {
      setError(errorMessage(e));
      if ([403, 409, 422, 503].includes(e?.response?.status)) { writeWorkspace(pendingKey, null); setPending(null); }
    } finally { setBusy(false); }
  };
  const retry = async item => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/requests/${item.id}/retry`);
      setHistory(previous => previous.map(row => row.id === data.id ? data : row));
      if (result?.id === data.id) setResult(data);
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const field = (name, label, type = 'text', extra = {}) => <label className="ad-field">
    <span>{label}</span><input name={name} aria-label={label} type={type} value={form[name]}
      onChange={change} required {...extra} /></label>;

  const targetLinkLabel = isAds && form.kind === 'ticket_sales'
    ? 'Mekanda.co bilet bağlantısı'
    : form.kind === 'venue' ? 'Mekân / Instagram bağlantısı' : 'Etkinlik / Instagram bağlantısı';

  return <div className="ad-request-page">
    <header>
      <p className="ad-eyebrow">{config?.agency_name || 'Mekanda PR Agency'}</p>
      <h1>Tanıtım &amp; Büyüme</h1>
      <p>Mekânın veya etkinliğin için reklam yönetimi ve influencer içerik hizmetlerinden birini seç.</p>
    </header>

    <section className="ad-card" aria-label="Tanıtım hizmetleri">
      <h2>Hizmet seç</h2>
      <div className="ad-kind-options">
        {Object.entries(SERVICE).map(([value, item]) => <label key={value}>
          <input type="radio" name="service" value={value} checked={form.service === value} onChange={change} disabled={busy || Boolean(pending) || review} />
          <span><strong>{item.title}</strong><small>{item.description}</small></span>
        </label>)}
      </div>
      {isAds ? <>
        <p>Sabit hizmet ücreti yok: vergi hariç planlanan toplam bütçenin <strong>%80’i reklam yayınına, %20’si ajans hizmetine</strong> ayrılır.</p>
        <p>Kampanya sonunda harcama ve mevcut sonuç raporu paylaşılır; satış, takipçi artışı veya organik paylaşım garantisi verilmez.</p>
      </> : <p>Fiyat, çekim kapsamı, içerik revizyonu, tarih ve {form.service === 'influencer_video_share' ? 'sayfa paylaşımının hesabı/yayın takvimi' : 'teslim koşulları'} ekip değerlendirmesinden sonra teklif ile netleşir. Bu form rezervasyon veya yayın onayı değildir.</p>}
    </section>

    {error && <p role="alert" className="ad-error">{error}</p>}
    {loading ? <p role="status">Hizmet bilgileri yükleniyor…</p> : !config
      ? <button className="action-primary" onClick={load}>Tekrar dene</button>
      : !config.available ? <p role="status" className="ad-card">Tanıtım hizmeti talepleri henüz kullanıma açılmadı.</p>
      : result ? <section className="ad-card" aria-label="Talep sonucu"><h2>Talebin kayıtlı</h2>
        <p><strong>{SERVICE[result.service || 'ads']?.title || 'Tanıtım hizmeti'}</strong></p>
        <p role="status">{statusLabel[result.email_status]}</p><p>Referans: {result.id}</p>
        <button className="action-secondary" onClick={() => {
          setResult(null); setAuthorized(false);
          setForm(previous => ({ ...previous, title: '', brief: '', target_url: '', budget_try: '' }));
        }}>Farklı bir talep oluştur</button>
      </section> : <form onSubmit={submit} className="ad-card">
        <fieldset disabled={busy || Boolean(pending) || review}>
          <legend>Neyi tanıtmak istiyorsun?</legend>{embedded && Object.keys(prefill).length > 0 && <p className="ad-prefill-note">Sohbetteki bilgileri taşıdım. Kontrol edip değiştirebilirsin; henüz talep gönderilmedi.</p>}
          <div className="ad-kind-options">{[['venue', 'Mekânımı tanıt'], ['ticket_sales', 'Etkinliğimi tanıt']].map(([value, label]) =>
            <label key={value}><input type="radio" name="kind" value={value} checked={form.kind === value} onChange={change} />{label}</label>)}</div>
          <div className="ad-form-grid">
            {field('title', form.kind === 'venue' ? 'Mekân adı' : 'Etkinlik adı', 'text', { maxLength: 150, minLength: 3 })}
            {field('region', 'Hedef şehir / ilçe', 'text', { maxLength: 150, minLength: 2 })}
            {field('target_url', targetLinkLabel, 'url', { maxLength: 500 })}
            {field('start_date', isAds ? 'İstenen reklam başlangıcı' : 'İstenen çekim / çalışma başlangıcı', 'date')}
            {form.kind === 'ticket_sales' && field('event_date', 'Etkinlik tarihi', 'date', { min: form.start_date })}
            {isAds && field('budget_try', 'Planlanan toplam reklam bütçesi (TL, vergi hariç)', 'number', {
              min: Math.max(0.01, Number(config.minimum_budget_try || 0)), max: 1000000, step: '0.01'
            })}
          </div>

          {budget && <div className="ad-budget" aria-live="polite">
            <p>Reklam yayını <strong>{money(budget.media)}</strong></p>
            <p>Ajans hizmeti (%20) <strong>{money(budget.agency)}</strong></p>
            <p>Vergi hariç toplam <strong>{money(budget.total)}</strong></p>
          </div>}
          {isAds && Number(config.minimum_budget_try) > 0 && <p>Minimum planlanan toplam bütçe: {money(config.minimum_budget_try)}.</p>}

          <label className="ad-field"><span>Hedefin ve kısa açıklaman</span>
            <textarea name="brief" aria-label="Hedefin ve kısa açıklaman" value={form.brief} onChange={change}
              required minLength={10} maxLength={2000} rows={4} /></label>

          <h2>Ekip sana nasıl ulaşsın?</h2>
          <div className="ad-form-grid">
            {field('contact_name', 'Ad soyad', 'text', { maxLength: 100, minLength: 2, autoComplete: 'name' })}
            {field('contact_email', 'İletişim e-postası', 'email', { autoComplete: 'email' })}
            {field('phone', 'Telefon', 'tel', { minLength: 10, maxLength: 25, autoComplete: 'tel' })}
          </div>

          <p>Hizmet markası: <strong>{config.agency_name}</strong>. Talebini bu hizmeti yürüten ekip değerlendirecek. <a href={config.privacy_notice_url} target="_blank" rel="noopener noreferrer">Veri paylaşımı hakkında aydınlatma metni</a></p>
          <label className="ad-consent"><input type="checkbox" checked={authorized} onChange={e => setAuthorized(e.target.checked)} required />
            <span>Bu talebi iletmeye yetkiliyim; formdaki iletişim ve tanıtım bilgilerimin talebimi değerlendirmesi ve benimle iletişime geçmesi için {config.agency_name} hizmet ekibiyle paylaşılmasını istiyorum.</span>
          </label>
        </fieldset>

        {pending && <p role="status">Önceki gönderimin sonucu belirsiz; aynı talep numarasıyla yeniden kontrol edilecek.</p>}
        {review ? <section aria-label="Tanıtım hizmeti gönderim onayı">
          <h2>Ekibe gönderilecek talep</h2>
          <p><strong>{SERVICE[form.service].title}</strong> · {form.title}{budget ? ` · ${money(budget.total)}` : ''}</p>
          <p>İletişim bilgilerin ve talep içeriğin hizmet ekibine e-posta ile iletilecek. Gönderilen e-posta geri alınamaz.</p>
          <button className="action-primary" type="button" disabled={busy} onClick={e => submit(e, true)}>Onayla ve talebi gönder</button>
          <button className="action-secondary" type="button" disabled={busy} onClick={() => setReview(false)}>Düzenlemeye dön</button>
        </section> : <button className="action-primary" type="submit"
          disabled={busy || !authorized || (isAds && !budget)}>
          {busy ? 'Talep kaydediliyor…' : pending ? 'Aynı talebi tekrar dene' : embedded ? 'Gönderim özetini gör' : 'Talebimi ilet'}
        </button>}
      </form>}

    {history.length > 0 && <section className="ad-card"><h2>Tanıtım taleplerim</h2>
      <button className="action-secondary" onClick={load} disabled={busy || loading}>Durumu yenile</button>
      <ul className="ad-history">{history.map(item => <li key={item.id}>
        <strong>{item.title}</strong>
        <p>{SERVICE[item.service || 'ads']?.title || 'Tanıtım hizmeti'}{item.budget?.total_try ? ` · ${money(item.budget.total_try)}` : ''} · {statusLabel[item.email_status]}</p>
        {['failed', 'pending', 'sending'].includes(item.email_status) && <button className="action-secondary" disabled={busy} onClick={() => retry(item)}>Gönderimi tekrar dene</button>}
      </li>)}</ul>
    </section>}
  </div>;
}
